import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchOutlined as SearchOutlinedIcon } from '@frontierag/frontier-ui-library/icons';
import { createFilterOptions } from '@frontierag/frontier-ui-library';
import { SUBGROUP_AUTOCOMPLETE_LIMIT } from 'appConstants';
import DynamicAutocomplete from 'components/DynamicAutocomplete';
import { useProducts } from 'contexts/Products';

import type { IProductSubgroup, ISubgroupOption } from './types';

const getOptionLabel = (option: ISubgroupOption | string): string =>
  typeof option === 'object' && option?.subGroupDescription ? option.subGroupDescription : '';

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  ignoreCase: true,
  trim: true,
  limit: SUBGROUP_AUTOCOMPLETE_LIMIT,
  stringify: (option: ISubgroupOption) => getOptionLabel(option),
});

export default function ProductSubgroup({
  disabled,
  textFieldProps,
  selectedSubgroup,
  onChange = () => {},
}: IProductSubgroup) {
  const { t } = useTranslation('ProductSubgroup');

  const handleOnChange = useCallback(
    (event, value) => {
      onChange(value);
    },
    [onChange],
  );

  const {
    state: {
      subGroups: { loading, data: subGroups = [], loaded, error },
    },
    actions: { loadSubgroups },
  } = useProducts();

  useEffect(() => {
    if (!loaded && !loading && !error) loadSubgroups({ limit: 0 });
  }, [loaded, loadSubgroups, loading, error]);

  const options = useMemo(
    () => subGroups.filter((subGroup) => subGroup.productsCount !== 0),
    [subGroups],
  );

  return (
    <DynamicAutocomplete
      disabled={disabled}
      textFieldProps={{
        label: t('label'),
        placeholder: t('placeholder'),
        ...textFieldProps,
      }}
      startAdornmentIcon={<SearchOutlinedIcon />}
      noOptionsText={t('noOptions')}
      value={selectedSubgroup}
      options={options}
      getOptionLabel={getOptionLabel}
      onChange={handleOnChange}
      loading={loading}
      displayPopupIcon
      filterOptions={filterOptions}
      isOptionEqualToValue={(option: ISubgroupOption, value) =>
        option.subGroupDescription === value.subGroupDescription
      }
    />
  );
}
